import { useQuery, keepPreviousData } from '@tanstack/react-query';
import * as intelligenceApi from '../api';

// =============================================================================
// Executive Overview Hooks
// =============================================================================

export function useExecutiveSummary(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'executive', 'summary', refresh],
    queryFn: () => intelligenceApi.getExecutiveSummary(refresh),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Alert Intelligence Hooks
// =============================================================================

export function useAlertSummary(period: 'today' | 'week' | 'month' | 'quarter' = 'week', refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'alerts', 'summary', period, refresh],
    queryFn: () => intelligenceApi.getAlertSummary(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAlertTrends(period: 'today' | 'week' | 'month' | 'quarter' = 'week', refresh = false) {
  // Map to API period format
  const apiPeriod = period === 'today' ? 'day' : period === 'quarter' ? 'month' : period;
  return useQuery({
    queryKey: ['intelligence', 'alerts', 'trends', period, refresh],
    queryFn: () => intelligenceApi.getAlertTrends(apiPeriod as 'day' | 'week' | 'month', refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAlertDistribution(period: 'today' | 'week' | 'month' | 'quarter' = 'week', refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'alerts', 'distribution', period, refresh],
    queryFn: () => intelligenceApi.getAlertDistribution(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAlertPatterns(period: 'today' | 'week' | 'month' | 'quarter' = 'week', refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'alerts', 'patterns', period, refresh],
    queryFn: () => intelligenceApi.getAlertPatterns(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Device Health Hooks
// =============================================================================

export function useDeviceHealth(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'devices', 'health', refresh],
    queryFn: () => intelligenceApi.getDeviceHealthSummary(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDeviceHealthSummary(refresh = false) {
  return useDeviceHealth(refresh);
}

export function useDeviceTrends(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'devices', 'trends', refresh],
    queryFn: () => intelligenceApi.getDeviceTrends('week', refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useFirmwareDistribution(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'devices', 'firmware', refresh],
    queryFn: () => intelligenceApi.getFirmwareDistribution(refresh),
    staleTime: 10 * 60 * 1000, // 10 minutes - changes less frequently
  });
}

export function useDevicesAttention(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'devices', 'attention', refresh],
    queryFn: () => intelligenceApi.getDevicesNeedingAttention(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDevicesNeedingAttention(refresh = false) {
  return useDevicesAttention(refresh);
}

// =============================================================================
// Customer Health Hooks
// =============================================================================

export function useCustomerHealth(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'customers', 'health', refresh],
    queryFn: () => intelligenceApi.getCustomerHealthList({ page: 0, pageSize: 100, refresh }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerHealthSummary(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'customers', 'summary', refresh],
    queryFn: () => intelligenceApi.getCustomerHealthSummary(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomerHealthList(params: {
  page?: number;
  pageSize?: number;
  risk?: 'all' | 'healthy' | 'warning' | 'critical';
  search?: string;
  refresh?: boolean;
} = {}) {
  const { page = 0, pageSize = 25, risk, search, refresh = false } = params;
  return useQuery({
    queryKey: ['intelligence', 'customers', 'list', page, pageSize, risk, search],
    queryFn: () => intelligenceApi.getCustomerHealthList({ page, pageSize, risk, search, refresh }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useCustomerDetail(customerId: string, refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'customers', 'detail', customerId, refresh],
    queryFn: () => intelligenceApi.getCustomerDetail(customerId, refresh),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Account Health Hooks (V4 PostgreSQL)
// =============================================================================

export function useAllAccounts() {
  return useQuery({
    queryKey: ['accounts', 'all-names'],
    queryFn: () => intelligenceApi.getAllAccountNames(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccountHealthOverview(params: {
  page?: number;
  pageSize?: number;
  risk?: string;
  search?: string;
  sortKey?: string;
  sortDirection?: 1 | -1;
} = {}) {
  const { page = 0, pageSize = 25, risk, search, sortKey, sortDirection } = params;
  return useQuery({
    queryKey: ['accounts', 'health-overview', page, pageSize, risk, search, sortKey, sortDirection],
    queryFn: () => intelligenceApi.getAccountHealthOverview({
      page,
      pageSize,
      risk,
      search,
      sortKey,
      sortDirection,
    }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAccountDashboard(accountId: string) {
  return useQuery({
    queryKey: ['accounts', 'dashboard', accountId],
    queryFn: () => intelligenceApi.getAccountDashboard(accountId),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccountHealthHistory(accountId: string, days = 30) {
  return useQuery({
    queryKey: ['accounts', 'health-history', accountId, days],
    queryFn: () => intelligenceApi.getAccountHealthHistory(accountId, days),
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Compliance Hooks
// =============================================================================

export function useComplianceScore(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'compliance', 'score', refresh],
    queryFn: () => intelligenceApi.getComplianceSummary(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useComplianceSummary(refresh = false) {
  return useComplianceScore(refresh);
}

export function useComplianceTrends(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'compliance', 'trends', refresh],
    queryFn: () => intelligenceApi.getComplianceTrends('month', refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useComplianceGaps(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'compliance', 'gaps', refresh],
    queryFn: () => intelligenceApi.getComplianceGaps(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCalibrationDue(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'compliance', 'calibration', refresh],
    queryFn: () => intelligenceApi.getCalibrationDue(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Operations Hooks
// =============================================================================

export function useOperationsSummary(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'operations', 'summary', refresh],
    queryFn: () => intelligenceApi.getOperationsSummary(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOperationsTrends(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'operations', 'trends', refresh],
    queryFn: () => intelligenceApi.getTicketTrends('week', refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTicketTrends(period: 'day' | 'week' | 'month' = 'week', refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'operations', 'trends', period, refresh],
    queryFn: () => intelligenceApi.getTicketTrends(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSlaCompliance(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'operations', 'sla', refresh],
    queryFn: () => intelligenceApi.getSlaCompliance(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkloadAnalysis(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'operations', 'workload', refresh],
    queryFn: () => intelligenceApi.getWorkloadAnalysis(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTicketsByCategory(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'operations', 'categories', refresh],
    queryFn: () => intelligenceApi.getTicketsByCategory(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTicketAging(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'operations', 'aging', refresh],
    queryFn: () => intelligenceApi.getTicketAging(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Battery Monitoring Hooks
// =============================================================================

import type { BatteryMonitoringFilters } from '../types';

export function useBatteryMonitoring(filters: BatteryMonitoringFilters = {}) {
  return useQuery({
    queryKey: [
      'intelligence', 'devices', 'battery-monitoring',
      filters.organisation, filters.site, filters.voltage_min, filters.voltage_max,
      filters.base_color, filters.power_source, filters.status, filters.search,
      filters.page, filters.limit, filters.sort_by, filters.sort_order,
    ],
    queryFn: () => intelligenceApi.getBatteryMonitoringData(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBatteryMonitoringAccounts() {
  return useQuery({
    queryKey: ['intelligence', 'devices', 'battery-monitoring', 'accounts'],
    queryFn: () => intelligenceApi.getBatteryMonitoringAccounts(),
    staleTime: 10 * 60 * 1000,
  });
}

// =============================================================================
// Communications Hooks
// =============================================================================

import type { PeriodFilter } from '../api';

export function useCommunicationsOverview(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'overview', period, refresh],
    queryFn: () => intelligenceApi.getCommunicationsOverview(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCommunicationsTrends(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'trends', period, refresh],
    queryFn: () => intelligenceApi.getCommunicationsTrends(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// Email Analytics Hooks
export function useEmailDeliveryStats(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'email', 'stats', period, refresh],
    queryFn: () => intelligenceApi.getEmailDeliveryStats(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useEmailByDomain(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'email', 'domains', period, refresh],
    queryFn: () => intelligenceApi.getEmailByDomain(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useEmailBounceBreakdown(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'email', 'bounces', period, refresh],
    queryFn: () => intelligenceApi.getEmailBounceBreakdown(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// SMS Analytics Hooks
export function useSmsDeliveryStats(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'sms', 'stats', period, refresh],
    queryFn: () => intelligenceApi.getSmsDeliveryStats(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSmsByCountry(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'sms', 'countries', period, refresh],
    queryFn: () => intelligenceApi.getSmsByCountry(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSmsErrorBreakdown(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'sms', 'errors', period, refresh],
    queryFn: () => intelligenceApi.getSmsErrorBreakdown(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// Calls Analytics Hooks
export function useCallsOutcomeStats(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'calls', 'stats', period, refresh],
    queryFn: () => intelligenceApi.getCallsOutcomeStats(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// Alert Sendouts Analytics Hooks
export function useAlertSlaCompliance(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'alerts', 'sla', period, refresh],
    queryFn: () => intelligenceApi.getAlertSlaCompliance(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAlertSendoutsByMethod(
  period: PeriodFilter = 'week',
  refresh = false
) {
  return useQuery({
    queryKey: ['intelligence', 'communications', 'alerts', 'methods', period, refresh],
    queryFn: () => intelligenceApi.getAlertSendoutsByMethod(period, refresh),
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Deal Insights Hooks
// =============================================================================

import type { DealFilters } from '../api';

export function useDealSummary(filters: DealFilters = {}) {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'summary', filters],
    queryFn: () => intelligenceApi.getDealSummary(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDealsByStage(pipeline?: string) {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'by-stage', pipeline],
    queryFn: () => intelligenceApi.getDealsByStage(pipeline),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDealsByType(pipeline?: string) {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'by-type', pipeline],
    queryFn: () => intelligenceApi.getDealsByType(pipeline),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDealsByPipeline() {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'by-pipeline'],
    queryFn: () => intelligenceApi.getDealsByPipeline(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDealTrends(
  period: 'week' | 'month' | 'quarter' = 'month',
  pipeline?: string
) {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'trends', period, pipeline],
    queryFn: () => intelligenceApi.getDealTrends(period, pipeline),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTopDeals(limit = 10, pipeline?: string) {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'top', limit, pipeline],
    queryFn: () => intelligenceApi.getTopDeals(limit, pipeline),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDealsClosingSoon(days = 30, pipeline?: string) {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'closing-soon', days, pipeline],
    queryFn: () => intelligenceApi.getDealsClosingSoon(days, pipeline),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDealPipelines() {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'pipelines'],
    queryFn: () => intelligenceApi.getDealPipelines(),
    staleTime: 10 * 60 * 1000, // Pipelines change less frequently
  });
}

export function useDealStages(pipeline?: string) {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'stages', pipeline],
    queryFn: () => intelligenceApi.getDealStages(pipeline),
    staleTime: 10 * 60 * 1000,
    enabled: true, // Always fetch, filter by pipeline if provided
  });
}

export function useOwnerPerformance() {
  return useQuery({
    queryKey: ['intelligence', 'deals', 'owner-performance'],
    queryFn: () => intelligenceApi.getOwnerPerformance(),
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Customer Expansion Intelligence Hooks
// =============================================================================

import type { ExpansionCustomersParams } from '../api';

export function useExpansionSummary(refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'expansion', 'summary', refresh],
    queryFn: () => intelligenceApi.getExpansionSummary(refresh),
    staleTime: 5 * 60 * 1000,
  });
}

export function useExpansionCustomers(params: ExpansionCustomersParams = {}) {
  const { page = 0, pageSize = 25, sort, order, category, search, refresh = false } = params;
  return useQuery({
    queryKey: ['intelligence', 'expansion', 'customers', page, pageSize, sort, order, category, search, refresh],
    queryFn: () => intelligenceApi.getExpansionCustomers({ page, pageSize, sort, order, category, search, refresh }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useExpansionCustomerDetail(customerId: string, refresh = false) {
  return useQuery({
    queryKey: ['intelligence', 'expansion', 'customer', customerId, refresh],
    queryFn: () => intelligenceApi.getExpansionCustomerDetail(customerId, refresh),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Re-export hooks from separate files
// =============================================================================

// Calibration Certificates hooks
export * from './useCalibrationCertificates';
export * from './useSavedViews';
export * from './useCalibrationFilters';

// Period filtering hooks
export * from './usePeriodFilter';
